import { Link, NavLink, useLocation } from 'react-router-dom';
import { User, LogIn, LogOut, Settings, Heart, Home, Shield, Users, Bell, Menu, X } from 'lucide-react';
import { useAuth } from '../../features/auth/AuthContext';
import { EmailVerificationNotice, useEmailVerification } from '../../features/auth/EmailVerificationNotice';
import { useState, useEffect } from 'react';
import { getUserNotifications, markNotificationRead, UserNotification } from '../../features/properties/../../shared/services/submissionService';

const NAV_ITEMS = [
	{ to: '/', label: 'Home' },
	{ to: '/properties', label: 'Properties' },
	{ to: '/featured', label: 'Featured' },
	{ to: '/blog', label: 'Blog' },
	{ to: '/about', label: 'About us' },
	{ to: '/contact', label: 'Contact us' },
];

export default function Header() {
	const { user, signOut, isAdmin } = useAuth();
	const { blocked: emailVerificationBlocked } = useEmailVerification();
	const [showUserMenu, setShowUserMenu] = useState(false);
	const [notifications, setNotifications] = useState<UserNotification[]>([]);
	const [showNotifications, setShowNotifications] = useState(false);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const location = useLocation();
	const unreadCount = notifications.filter((n) => !n.read).length;

	// Close the mobile menu whenever the route changes
	useEffect(() => {
		setShowMobileMenu(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!user) { setNotifications([]); return; }
		getUserNotifications(user.uid).then(setNotifications).catch(() => {});
	}, [user]);

	const handleMarkRead = async (id: string) => {
		await markNotificationRead(id);
		setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
	};

	const handleSignOut = async () => {
		await signOut();
		setShowUserMenu(false);
	};

	return (
		<header className="sticky top-0 z-50 border-b bg-white shadow-sm">
			<div className="mx-auto max-w-6xl px-4 py-4 md:py-6 flex items-center justify-between">
				<Link to="/" className="flex items-center">
					<img 
						src="/logo.png"
						alt="POP3i Properties" 
						className="h-20 w-auto object-contain" 
						style={{ maxHeight: '80px' }}
					/>
				</Link>
				
				<nav className="hidden md:flex items-center gap-6 mt-3">
					{NAV_ITEMS.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							className={({ isActive }) =>
								`relative pb-1 text-base transition-colors ${
									isActive
										? 'font-semibold text-brand-700 after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-brand-600'
										: 'font-medium text-gray-700 hover:text-brand-700'
								}`
							}
						>
							{item.label}
						</NavLink>
					))}
				</nav>

				<div className="flex items-center gap-2 md:gap-3 md:mt-3">
					{user && (
						<div className="relative">
							<button
								onClick={() => setShowNotifications(!showNotifications)}
								className="relative p-2 text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-100"
							>
								<Bell className="h-5 w-5" />
								{unreadCount > 0 && (
									<span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center leading-none">
										{unreadCount > 9 ? '9+' : unreadCount}
									</span>
								)}
							</button>
							{showNotifications && (
								<div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border z-50 overflow-hidden">
									<div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
										<span className="font-semibold text-gray-800 text-sm">Notifications</span>
										{unreadCount > 0 && (
											<span className="text-xs text-gray-500">{unreadCount} unread</span>
										)}
									</div>
									<div className="max-h-80 overflow-y-auto">
										{notifications.length === 0 ? (
											<p className="px-4 py-6 text-sm text-center text-gray-500">No notifications</p>
										) : (
											notifications.slice(0, 10).map((n) => (
												<button
													key={n.id}
													onClick={() => handleMarkRead(n.id)}
													className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-brand-50' : ''}`}
												>
													<div className="flex items-start gap-2">
														<div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
															n.type === 'success' ? 'bg-green-500' :
															n.type === 'error' ? 'bg-red-500' : 'bg-brand-500'
														}`} />
														<div>
															<p className="text-sm font-semibold text-gray-800">{n.title}</p>
															<p className="text-xs text-gray-600 mt-0.5">{n.message}</p>
															<p className="text-xs text-gray-400 mt-1">{n.createdAt.toLocaleDateString('en-IN')}</p>
														</div>
													</div>
												</button>
											))
										)}
									</div>
								</div>
							)}
						</div>
					)}
					{user ? (
						<div className="relative">
							<button
								onClick={() => setShowUserMenu(!showUserMenu)}
								className="flex items-center gap-2 text-sm text-gray-700 hover:text-brand-700 px-3 py-2 rounded-md hover:bg-brand-50 transition-colors"
							>
								<User className="h-4 w-4" />
								<span className="hidden sm:inline">{user.name}</span>
							</button>

							{showUserMenu && (
								<div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border z-50">
									<div className="py-1">
										<div className="px-4 py-2 text-sm text-gray-500 border-b">
											{user.email}
										</div>
										<Link
											to="/profile"
											className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
											onClick={() => setShowUserMenu(false)}
										>
											<Settings className="h-4 w-4" />
											Profile Settings
										</Link>
										<Link
											to="/my-properties"
											className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
											onClick={() => setShowUserMenu(false)}
										>
											<Home className="h-4 w-4" />
											My Properties
										</Link>
										<Link
											to="/favorites"
											className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
											onClick={() => setShowUserMenu(false)}
										>
											<Heart className="h-4 w-4" />
											My Favorites
										</Link>
										{isAdmin && (
											<Link
												to="/admin"
												className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
												onClick={() => setShowUserMenu(false)}
											>
												<Shield className="h-4 w-4" />
												Admin Panel
											</Link>
										)}
										{isAdmin && (
											<Link
												to="/admin/users"
												className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
												onClick={() => setShowUserMenu(false)}
											>
												<Users className="h-4 w-4" />
												User Management
											</Link>
										)}
										<button
											onClick={handleSignOut}
											className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
										>
											<LogOut className="h-4 w-4" />
											Sign Out
										</button>
									</div>
								</div>
							)}
						</div>
					) : (
						<>
							<Link
								to="/login"
								className="flex items-center gap-1 text-sm text-gray-700 hover:text-brand-700 px-3 py-1 rounded-md hover:bg-brand-50"
							>
								<LogIn className="h-4 w-4" />
								<span className="hidden sm:inline">Login</span>
							</Link>
							<Link
								to="/register"
								className="bg-brand-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-brand-700 transition-colors flex items-center gap-1"
							>
								<User className="h-4 w-4" />
								<span className="hidden sm:inline">Register</span>
							</Link>
						</>
					)}

					{/* Mobile menu toggle */}
					<button
						onClick={() => setShowMobileMenu(!showMobileMenu)}
						className="md:hidden p-2 text-gray-700 hover:text-brand-700 rounded-md hover:bg-gray-100"
						aria-label="Toggle navigation menu"
					>
						{showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
					</button>
				</div>
			</div>

			{/* Mobile navigation menu */}
			{showMobileMenu && (
				<nav className="md:hidden border-t bg-white">
					<div className="px-4 py-2">
						{NAV_ITEMS.map((item) => (
							<NavLink
								key={item.to}
								to={item.to}
								onClick={() => setShowMobileMenu(false)}
								className={({ isActive }) =>
									`block px-3 py-3 rounded-md text-base font-medium border-b last:border-b-0 border-gray-100 ${
										isActive ? 'text-brand-700 bg-brand-50' : 'text-gray-700 hover:text-brand-700 hover:bg-gray-50'
									}`
								}
							>
								{item.label}
							</NavLink>
						))}
					</div>
				</nav>
			)}
			{emailVerificationBlocked && <EmailVerificationNotice placement="banner" />}
		</header>
	);
}
